<template>
  <div class="bz-selector-page">
    <div class="bz-pagehead">
      <div class="bz-pagehead-l">
        <h1>Company Profile</h1>
        <beta-pill product="company_profile" />
      </div>
    </div>

    <div class="bzB rail">
      <div class="bzB-steps">
        <step-card :n="1" title="Company" icon="storefront" :is-open="open === 1" @toggle="toggle(1)"
          :value="company && company.label">
          <company-step :selected="company" :favorites="favorites"
            @select="onSelect" @toggle-fav="toggleFav" />
        </step-card>
        <div class="bz-flow-note">
          <i class="material-icons" style="font-size:15px;line-height:1;">verified</i>
          That's the whole flow — no separate review screen. The summary on the right is the review;
          Access Now opens the profile directly.
        </div>
      </div>

      <div class="bz-rail">
        <div class="bz-rail-h">Your profile</div>
        <div class="bz-rail-list">
          <button class="bz-rail-row" :class="{ filled: !!company }" type="button" @click="toggle(1)">
            <span class="bz-rail-k">Company</span>
            <span class="bz-rail-v">{{ company ? company.label : 'Not selected' }}</span>
            <i class="material-icons" style="font-size:14px;line-height:1;color:var(--bzc-faint);">edit</i>
          </button>
          <button class="bz-rail-row" :class="{ filled: !!(company && (company.street || company.zip)) }" type="button" @click="toggle(1)">
            <span class="bz-rail-k">Location</span>
            <span class="bz-rail-v">{{ locationLabel }}</span>
            <i class="material-icons" style="font-size:14px;line-height:1;color:var(--bzc-faint);">edit</i>
          </button>
          <button v-if="company && company.industry" class="bz-rail-row filled" type="button" @click="toggle(1)">
            <span class="bz-rail-k">Industry</span>
            <span class="bz-rail-v">{{ company.industry }}</span>
            <i class="material-icons" style="font-size:14px;line-height:1;color:var(--bzc-faint);">edit</i>
          </button>
          <div v-if="company && company.employees" class="bz-rail-row filled">
            <span class="bz-rail-k">Est. size</span>
            <span class="bz-rail-v">{{ company.employees }} employees</span>
          </div>
        </div>
        <div class="bz-rail-foot">
          <div class="bz-status" :class="company ? 'ok' : 'todo'">
            <i class="material-icons" style="font-size:16px;line-height:1;">{{ company ? 'check_circle' : 'info' }}</i>
            <span v-if="company"><b>Ready</b> · profile available now</span>
            <span v-else>Search and select a company to continue</span>
          </div>
          <div class="bz-cta-wrap">
            <button class="bz-btn primary" type="button" :disabled="!company || ctaDone || submitting" @click="access">
              <template v-if="ctaDone">
                <i class="material-icons" style="font-size:16px;line-height:1;">check</i>Added — opening profile…
              </template>
              <template v-else-if="submitting">Processing…</template>
              <template v-else>
                Access Now<i class="material-icons" style="font-size:16px;line-height:1;">arrow_forward</i>
              </template>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Company Profile Beta — one step, rail is the review, no confirm modal.
// Access: POST /user/create/companyProfile {locnum, zip} → cart/accessProduct →
// Cart or /companies/{id}/profile/auxpage (same wiring as classic CompanySearch).
import StepCard from './StepCard.vue';
import BetaPill from './BetaPill.vue';
import CompanyStep from './CompanyStep.vue';
import { createCompanyProfile } from './model';
import { trackSelectorViewed, trackSelectorCompleted, trackCompanySelected } from './analytics';
import './beta-selector.css';

export default {
  name: 'company-page',
  components: { StepCard, BetaPill, CompanyStep },
  data() {
    return { company: null, favorites: [], open: 1, ctaDone: false, submitting: false };
  },
  computed: {
    locationLabel() {
      if (!this.company) return '—';
      const place = [this.company.city, this.company.state].filter(Boolean).join(', ');
      return [this.company.street, place].filter(Boolean).join(', ') || (this.company.zip ? 'ZIP ' + this.company.zip : '—');
    },
  },
  created() {
    try {
      this.favorites = JSON.parse(localStorage.getItem(this.favKey()) || '[]');
    } catch (e) {
      this.favorites = [];
    }
  },
  mounted() {
    trackSelectorViewed('company_profile', 'beta');
  },
  methods: {
    favKey() {
      const uid = this.$store.getters['user/user_id'] || 'anon';
      return 'bzCompanyFavs:' + uid;
    },
    toggle(n) {
      this.open = this.open === n ? 0 : n;
    },
    onSelect({ company, method }) {
      this.company = company;
      this.open = 0;
      this.ctaDone = false;
      trackCompanySelected(method);
    },
    toggleFav(c) {
      const i = this.favorites.findIndex((f) => f.locnum === c.locnum && f.zip === c.zip);
      if (i === -1) this.favorites.push({ label: c.label, locnum: c.locnum, zip: c.zip, industry: c.industry });
      else this.favorites.splice(i, 1);
      localStorage.setItem(this.favKey(), JSON.stringify(this.favorites));
    },
    async access() {
      if (!this.company || this.submitting || this.ctaDone) return;
      this.submitting = true;
      try {
        const id = await createCompanyProfile(this.company.locnum, this.company.zip);
        const res = await this.$store.dispatch('cart/accessProduct', { product_id: id, access_legacy: false });
        trackSelectorCompleted('company_profile', 'beta');
        this.ctaDone = true;
        if (res.cart) {
          this.$router.push({ name: 'Cart', params: { id } });
        } else if (res.access || (res.error && res.errorCode === 'product_already_accessed')) {
          this.$router.push({ path: '/companies/' + id + '/profile/auxpage' });
        }
      } catch (e) {
        this.ctaDone = false;
        // Daily cap: the global dialog already explained it; skip the generic alert.
        if (e && e.dailyLimit) return;
        this.$store.dispatch('alerts/error', 'Something went wrong accessing this profile. Please try again.');
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
